import { getBaseUrl } from "simplydo/core/helpers";
import { i18n } from "simplydo/core/i18n";
import {
  getImageUrl,
  groupCoverImage,
  ideaCoverImage,
  userAvatarUrl,
} from "simplydo/core/images";
import { DefaultCapabilities } from "simplydo/react/richtext/editor/extensions/default";
import type {
  EditorCapabilities,
  UploadFileFn,
} from "simplydo/react/richtext/editor/types";

import api from "api";

const uploadFile: UploadFileFn = (file, uploadOptions) => {
  const regex = /[^a-zA-Z0-9_.]/g;
  const name = file.name.replace(regex, "-");
  const { forType, forId } = uploadOptions;

  return new Promise((resolve, reject) => {
    api.uploads.generateFileUploadRequest(
      { forType, forId, name, size: file.size, type: file.type },
      (response) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", response.signedRequest);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            // progress
          }
        });
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              resolve(response.signedDownloadRequest);
            } else {
              try {
                const errorResponse = JSON.parse(xhr.responseText);
                reject(
                  new Error(errorResponse.message || "Unable to upload file"),
                );
              } catch {
                reject(new Error("Unable to upload file"));
              }
            }
          }
        };
        xhr.send(file);
      },
      (err) => {
        reject(new Error(err.message || "Unable to upload file"));
      },
    );
  });
};

const FullPreset: EditorCapabilities = {
  ...DefaultCapabilities,
  bulletList: true,
  orderedList: true,
  bold: true,
  strike: true,
  italic: true,
  underline: true,
  link: { autoLink: true },
  image: { uploadFile },
  mentions: [
    {
      char: "@",
      name: "users",
      formatMention: (user) => ({
        href: `/users/${user._id}`,
        id: user._id,
        label: user?.profile?.fullName,
        description:
          user?.profile?.department || user?.profile?.jobTitle || "User",
        image: userAvatarUrl(user),
      }),
    },
    {
      char: "$",
      name: "ideas",
      formatMention: (idea) => ({
        href: `/ideas/${idea._id}`,
        id: idea._id,
        label: idea?.name,
        description: `${i18n.t("common:capitalise", { key: "generic.idea" })} for ${idea?.ownerChallenge?.name}`,
        image: ideaCoverImage(idea),
      }),
    },
    {
      char: "$",
      name: "challenges",
      formatMention: (challenge) => ({
        href: `/challenges/${challenge._id}`,
        id: challenge._id,
        label: challenge?.name,
        description: `${challenge.stage === "published" ? "Open" : "Closed"} ${i18n.t("generic.challenge")}`,
        image: getImageUrl(
          challenge,
          ["coverImage", "bannerImage"],
          ["200x200"],
        ),
      }),
    },
    {
      char: "$",
      name: "organisations",
      formatMention: (org) => ({
        href: getBaseUrl(import.meta.env.VITE_STAGE, org),
        id: org._id,
        label: org?.name,
        description: "Organisation",
        image: org?.darkLogoUrl,
      }),
    },
    {
      char: "$",
      name: "groups",
      formatMention: (group) => ({
        href: `/groups/${group._id}`,
        id: group._id,
        label: group?.name,
        description: i18n.t("common:capitalise", { key: "generic.group" }),
        image: groupCoverImage(group),
      }),
    },
  ],
  emoji: true,
  alignment: true,
  video: {},
  iframe: true,
  file: {
    onClick: (src: string | null) => {
      if (src) {
        window.open(src, "_blank");
      }
    },
    uploadFile,
  },
};

export default FullPreset;
